import { apiClient } from "../axiosInstance";

class BlogService {
  constructor() {
    this.baseUrl = "/blogs";
    this.adminBaseUrl = "/blogs/admin/blogs";
  }

  // ==================== PUBLIC ENDPOINTS ====================

  async getPublishedBlogs(params = {}) {
    try {
      const response = await apiClient.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get published blogs error:", error);
      throw error;
    }
  }

  async getFeaturedBlogs(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/featured`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get featured blogs error:", error);
      throw error;
    }
  }

  async getPopularBlogs(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/popular`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get popular blogs error:", error);
      throw error;
    }
  }

  async getTrendingBlogs(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/trending`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get trending blogs error:", error);
      throw error;
    }
  }

  async getBlogsByCategoryId(categoryId, params = {}) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/category/${categoryId}`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Get blogs by category ID error:", error);
      throw error;
    }
  }

  async getBlogsByCategorySlug(categorySlug, params = {}) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/category/slug/${encodeURIComponent(categorySlug)}`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Get blogs by category slug error:", error);
      throw error;
    }
  }

  async getBlogsByTag(tag, params = {}) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/tag/${encodeURIComponent(tag)}`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Get blogs by tag error:", error);
      throw error;
    }
  }

  async searchBlogs(query, params = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/search`, {
        params: { q: query, ...params },
      });
      return response.data;
    } catch (error) {
      console.error("Search blogs error:", error);
      throw error;
    }
  }

  async getPopularTags(limit = 20) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/popular-tags`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get popular tags error:", error);
      throw error;
    }
  }

  async getArchiveByMonth() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/archive`);
      return response.data;
    } catch (error) {
      console.error("Get archive by month error:", error);
      throw error;
    }
  }

  async getBlogBySlug(slug) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${slug}`);
      return response.data;
    } catch (error) {
      console.error("Get blog by slug error:", error);
      throw error;
    }
  }

  async toggleLike(id) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/like`);
      return response.data;
    } catch (error) {
      console.error("Toggle like error:", error);
      throw error;
    }
  }

  async toggleSave(id) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/save`);
      return response.data;
    } catch (error) {
      console.error("Toggle save error:", error);
      throw error;
    }
  }

  async toggleBookmark(id) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/bookmark`);
      return response.data;
    } catch (error) {
      console.error("Toggle bookmark error:", error);
      throw error;
    }
  }

  async shareBlog(id) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/share`);
      return response.data;
    } catch (error) {
      console.error("Share blog error:", error);
      throw error;
    }
  }

  async setReaction(id, reactionType) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/reaction`, {
        reactionType,
      });
      return response.data;
    } catch (error) {
      console.error("Set reaction error:", error);
      throw error;
    }
  }

  async removeReaction(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}/reaction`);
      return response.data;
    } catch (error) {
      console.error("Remove reaction error:", error);
      throw error;
    }
  }

  async getReactionCounts(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/reactions`);
      return response.data;
    } catch (error) {
      console.error("Get reaction counts error:", error);
      throw error;
    }
  }

  async addComment(id, content, userName = null, userEmail = null) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/comments`, {
        content,
        userName,
        userEmail,
      });
      return response.data;
    } catch (error) {
      console.error("Add comment error:", error);
      throw error;
    }
  }

  async addReply(commentId, content, userName = null, userEmail = null) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/comments/${commentId}/replies`,
        {
          content,
          userName,
          userEmail,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Add reply error:", error);
      throw error;
    }
  }

  async getBlogComments(id, params = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/comments`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Get blog comments error:", error);
      throw error;
    }
  }

  async likeComment(commentId) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/comments/${commentId}/like`,
      );
      return response.data;
    } catch (error) {
      console.error("Like comment error:", error);
      throw error;
    }
  }

  async approveComment(commentId) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/comments/${commentId}/approve`,
      );
      return response.data;
    } catch (error) {
      console.error("Approve comment error:", error);
      throw error;
    }
  }

  async rejectComment(commentId) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/comments/${commentId}/reject`,
      );
      return response.data;
    } catch (error) {
      console.error("Reject comment error:", error);
      throw error;
    }
  }

  async deleteComment(commentId) {
    try {
      const response = await apiClient.delete(
        `${this.adminBaseUrl}/comments/${commentId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Delete comment error:", error);
      throw error;
    }
  }

  async votePoll(id, optionIndex) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/poll/vote`, {
        optionIndex,
      });
      return response.data;
    } catch (error) {
      console.error("Vote poll error:", error);
      throw error;
    }
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Create a new blog post with image upload (Admin only)
   */
  async createBlog(blogData, featuredImage = null, galleryImages = []) {
    try {
      let response;

      if (featuredImage || galleryImages.length > 0) {
        const formData = new FormData();

        Object.keys(blogData).forEach((key) => {
          if (blogData[key] !== undefined && blogData[key] !== null) {
            if (key === "tags" && Array.isArray(blogData[key])) {
              if (blogData[key].length > 0) {
                blogData[key].forEach((tag) => {
                  formData.append("tags", tag);
                });
              }
            } else if (typeof blogData[key] === "boolean") {
              formData.append(key, blogData[key].toString());
            } else if (
              typeof blogData[key] === "object" &&
              !(blogData[key] instanceof File)
            ) {
              formData.append(key, JSON.stringify(blogData[key]));
            } else {
              formData.append(key, blogData[key]);
            }
          }
        });

        if (featuredImage) {
          formData.append("featuredImage", featuredImage);
        }

        galleryImages.forEach((image) => {
          formData.append("galleryImages", image);
        });

        response = await apiClient.upload(`${this.adminBaseUrl}`, formData);
      } else {
        const jsonData = {
          ...blogData,
          status: blogData.status?.toLowerCase(), // Changed to lowercase
          tags: Array.isArray(blogData.tags) ? blogData.tags : [],
        };
        response = await apiClient.post(`${this.adminBaseUrl}`, jsonData);
      }

      return response.data;
    } catch (error) {
      console.error("Create blog error:", error);
      throw error;
    }
  }

  /**
   * Get all blogs with pagination and filters (Admin only)
   */
  async getAllBlogs(params = {}) {
    try {
      const response = await apiClient.get(this.adminBaseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get all blogs error:", error);
      throw error;
    }
  }

  /**
   * Get blog statistics (Admin only)
   */
  async getBlogStats() {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Get blog stats error:", error);
      throw error;
    }
  }

  async getTopAuthors(limit = 10) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/top-authors`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get top authors error:", error);
      throw error;
    }
  }

  async getTrendingPosts(days = 7, limit = 10) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/trending-posts`,
        {
          params: { days, limit },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Get trending posts error:", error);
      throw error;
    }
  }

  async getBlogsByAuthor(authorId, params = {}) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/author/${authorId}`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Get blogs by author error:", error);
      throw error;
    }
  }

  async getBlogByUuid(uuid) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/uuid/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Get blog by uuid error:", error);
      throw error;
    }
  }

  async getBlogById(id) {
    try {
      const response = await apiClient.get(`${this.adminBaseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get blog by id error:", error);
      throw error;
    }
  }

  /**
   * Update blog with image upload (Admin only)
   */
  async updateBlog(id, blogData, featuredImage = null, galleryImages = []) {
    try {
      let response;

      if (featuredImage || galleryImages.length > 0) {
        const formData = new FormData();

        Object.keys(blogData).forEach((key) => {
          if (blogData[key] !== undefined && blogData[key] !== null) {
            if (key === "tags" && Array.isArray(blogData[key])) {
              if (blogData[key].length > 0) {
                blogData[key].forEach((tag) => {
                  formData.append("tags", tag);
                });
              }
            } else if (typeof blogData[key] === "boolean") {
              formData.append(key, blogData[key].toString());
            } else if (
              typeof blogData[key] === "object" &&
              !(blogData[key] instanceof File)
            ) {
              formData.append(key, JSON.stringify(blogData[key]));
            } else {
              formData.append(key, blogData[key]);
            }
          }
        });

        if (featuredImage) {
          formData.append("featuredImage", featuredImage);
        }

        galleryImages.forEach((image) => {
          formData.append("galleryImages", image);
        });

        response = await apiClient.upload(
          `${this.adminBaseUrl}/${id}`,
          formData,
          {},
          "patch",
        );
      } else {
        const jsonData = {
          ...blogData,
          status: blogData.status?.toLowerCase(), // Changed to lowercase
          tags: Array.isArray(blogData.tags) ? blogData.tags : [],
        };
        response = await apiClient.patch(
          `${this.adminBaseUrl}/${id}`,
          jsonData,
        );
      }

      return response.data;
    } catch (error) {
      console.error("Update blog error:", error);
      throw error;
    }
  }

  async publishBlog(id) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/publish`,
      );
      return response.data;
    } catch (error) {
      console.error("Publish blog error:", error);
      throw error;
    }
  }

  async scheduleBlog(id, scheduledAt) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/schedule`,
        { scheduledAt },
      );
      return response.data;
    } catch (error) {
      console.error("Schedule blog error:", error);
      throw error;
    }
  }

  async archiveBlog(id) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/archive`,
      );
      return response.data;
    } catch (error) {
      console.error("Archive blog error:", error);
      throw error;
    }
  }

  async restoreBlog(id) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/restore`,
      );
      return response.data;
    } catch (error) {
      console.error("Restore blog error:", error);
      throw error;
    }
  }

  async deleteBlog(id) {
    try {
      const response = await apiClient.delete(`${this.adminBaseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Delete blog error:", error);
      throw error;
    }
  }

  async bulkDeleteBlogs(ids) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/bulk-delete`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk delete blogs error:", error);
      throw error;
    }
  }

  async bulkRestoreBlogs(ids) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/bulk-restore`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk restore blogs error:", error);
      throw error;
    }
  }

  async bulkPublishBlogs(ids) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/bulk-publish`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk publish blogs error:", error);
      throw error;
    }
  }

  async bulkArchiveBlogs(ids) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/bulk-archive`,
        { ids },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk archive blogs error:", error);
      throw error;
    }
  }

  async bulkUpdateStatus(ids, status) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/bulk-status`,
        { ids, status },
      );
      return response.data;
    } catch (error) {
      console.error("Bulk update status error:", error);
      throw error;
    }
  }

  async getRelatedPosts(id, limit = 5) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/${id}/related`,
        { params: { limit } },
      );
      return response.data;
    } catch (error) {
      console.error("Get related posts error:", error);
      throw error;
    }
  }

  async updateRelatedPosts(id, relatedPostIds) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/related`,
        { relatedPostIds },
      );
      return response.data;
    } catch (error) {
      console.error("Update related posts error:", error);
      throw error;
    }
  }

  async generateTableOfContents(id) {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/${id}/generate-toc`,
      );
      return response.data;
    } catch (error) {
      console.error("Generate table of contents error:", error);
      throw error;
    }
  }

  async addPoll(id, question, options, endsAt) {
    try {
      const response = await apiClient.post(`${this.adminBaseUrl}/${id}/poll`, {
        question,
        options,
        endsAt,
      });
      return response.data;
    } catch (error) {
      console.error("Add poll error:", error);
      throw error;
    }
  }

  async toggleFeatured(id, isFeatured) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/toggle-featured`,
        { isFeatured },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle featured error:", error);
      throw error;
    }
  }

  async togglePopular(id, isPopular) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/toggle-popular`,
        { isPopular },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle popular error:", error);
      throw error;
    }
  }

  async toggleTrending(id, isTrending) {
    try {
      const response = await apiClient.patch(
        `${this.adminBaseUrl}/${id}/toggle-trending`,
        { isTrending },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle trending error:", error);
      throw error;
    }
  }

  async processScheduledPosts() {
    try {
      const response = await apiClient.post(
        `${this.adminBaseUrl}/process-scheduled`,
      );
      return response.data;
    } catch (error) {
      console.error("Process scheduled posts error:", error);
      throw error;
    }
  }

  // ==================== CATEGORY ENDPOINTS ====================

  async getBlogCategories(params = {}) {
    try {
      const response = await apiClient.get(`/blogs/admin/blogs/categories`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Get blog categories error:", error);
      throw error;
    }
  }

  async getBlogCategoryById(id) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/categories/${id}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get blog category by id error:", error);
      throw error;
    }
  }

  async getBlogCategoryBySlug(slug) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/categories/slug/${encodeURIComponent(slug)}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get blog category by slug error:", error);
      throw error;
    }
  }

  async createBlogCategory(categoryData, image = null) {
    try {
      let response;

      if (image) {
        const formData = new FormData();
        formData.append("name", categoryData.name);
        if (categoryData.description) {
          formData.append("description", categoryData.description);
        }
        formData.append("image", image);

        response = await apiClient.post(
          `${this.adminBaseUrl}/categories`,
          formData,
        );
      } else {
        response = await apiClient.post(
          `${this.adminBaseUrl}/categories`,
          categoryData,
        );
      }

      return response.data;
    } catch (error) {
      console.error("Create blog category error:", error);
      throw error;
    }
  }

  async updateBlogCategory(id, categoryData, image = null) {
    try {
      let response;

      if (image) {
        const formData = new FormData();
        if (categoryData.name) {
          formData.append("name", categoryData.name);
        }
        if (categoryData.description) {
          formData.append("description", categoryData.description);
        }
        formData.append("image", image);

        response = await apiClient.patch(
          `${this.adminBaseUrl}/categories/${id}`,
          formData,
        );
      } else {
        response = await apiClient.patch(
          `${this.adminBaseUrl}/categories/${id}`,
          categoryData,
        );
      }

      return response.data;
    } catch (error) {
      console.error("Update blog category error:", error);
      throw error;
    }
  }

  async deleteBlogCategory(id) {
    try {
      const response = await apiClient.delete(
        `${this.adminBaseUrl}/categories/${id}`,
      );
      return response.data;
    } catch (error) {
      console.error("Delete blog category error:", error);
      throw error;
    }
  }

  async getCategoriesWithCounts(params = {}) {
    try {
      const response = await apiClient.get(
        `${this.adminBaseUrl}/categories/with-counts`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Get categories with counts error:", error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  getStatusBadge(status) {
    const badges = {
      DRAFT: {
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: "FileText",
      },
      PUBLISHED: {
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: "CheckCircle",
      },
      SCHEDULED: {
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: "Clock",
      },
      ARCHIVED: {
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        icon: "Archive",
      },
    };
    return badges[status] || badges.DRAFT;
  }

  getStatusLabel(status) {
    const labels = {
      DRAFT: "Draft",
      PUBLISHED: "Published",
      SCHEDULED: "Scheduled",
      ARCHIVED: "Archived",
    };
    return labels[status] || status;
  }

  getReadingTime(content) {
    const wordsPerMinute = 200;
    const wordCount = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  }

  generateSlug(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }
}

export const blogService = new BlogService();
